import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../../popup/stations/Station.scss';
import { ONEDAY, ONEMIN, ONESEC, backgroundColor, formatDate, formatMinute, windDirectionChange, SFColors } from '../../../../../store/common';
import { useStoreContext } from '../../../../../store/context';
import arrow from '../../../../../assets/images/arrow.png';
import moment from 'moment-timezone';
import { alertMessages, humidityColors, temperatureColors, precip, windGustColors, solarRadiationColors, vpdColors } from '../../../../../constants';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { getWeatherMinMax, getWeatherObsStations } from 'src/apis/getWeatherCalls';
import { useAddAlert } from 'src/store/actions';
import TableHead from '@mui/material/TableHead';
import { Grid } from 'src/shared/grid';
import { Button, TableBody } from '@mui/material';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import LaunchIcon from '@mui/icons-material/Launch';
import { ROUTE_PATHS } from 'src/routes';

type HourlyTablePopUpProps = {
  id:any;
  stationMeta:any;
}

export const HourlyTablePopUp: React.FC<HourlyTablePopUpProps> = ({
  id, stationMeta
}) => {
  const [state, dispatch] = useStoreContext();
  const [currHr, setCurrHr] = useState('');
  const [modifiedContents, setModifiedContents] = useState<any[]>([]);
  const today = new Date();
  const [currDate, setCurrDate] = useState(today.toISOString().slice(0, today.toISOString().indexOf('T')))
  const addAlert = useAddAlert(dispatch);
  const scrollRef = useRef<HTMLTableElement | HTMLDivElement | null>(null);

  const fillMissingHours=useCallback((data: any[], startTime: any, endTime: any, responseMinMax: any[])=>{
    let minuteSet = 0;
    if(data.length>0){
      minuteSet = moment(data[0].observation_time).minute()
    }
    const filledData:any[] = [];
    
    let currentTime = moment.utc(startTime).set({hour:0, minute:0, second:0})
    const endMoment = moment.utc(endTime);

    const fillInTimeMinuteWhenEmpty = (currTime: any)=>{
      if(moment()>currTime){
        //observation
        const hr = moment(currentTime).utc().hour()
        if(minuteSet<30){
          return moment(currTime).set({hour: hr, minute: minuteSet}).utc().utcOffset(stationMeta.time_zone_offset).format();
        }
        else{
          return moment(currTime).subtract(1,'hour').set({minute: minuteSet}).utc().utcOffset(stationMeta.time_zone_offset).format();
        }
      }
      else{
        //Forcast
        return moment(currTime).utc().utcOffset(stationMeta.time_zone_offset).format()
      }
    }
    if(stationMeta?.time_zone_offset){
      while(currentTime.isBefore(endMoment)){
        const currentHour = currentTime.toISOString();
        const sfExists:any = responseMinMax.find(item=>item.summary_date ===  moment(currentTime).utc().utcOffset(stationMeta.time_zone_offset).format('YYYY-MM-DD'));
        const existingData:any = data.find(item=>item.display_hour === currentHour);
        if(existingData){
          filledData.push(existingData);
        }
        else{
          filledData.push({
            station_id: stationMeta.station_id,
            observation_time: currentHour,
            display_hour: currentHour,
            observation_time_lst: fillInTimeMinuteWhenEmpty(currentTime),
            display_hour_lst: moment(currentTime).utc().utcOffset(stationMeta.time_zone_offset).format(),
            hourly_precip: null,
            observation_type: moment()>currentTime? 'O':'F',
            peak_gust_dir:null,
            peak_gust_speed: null,
            relative_humidity: null,
            snow_flag:sfExists? sfExists.snow_flag: null,
            sol_rad: null,
            temperature: null,
            wind_direction:null,
            wind_speed:null,
            vapor_press_def: null,
          })
        }
        currentTime.add(1, 'hour');
      }
    }
    return filledData;
  },[stationMeta]);

  const weatherCallBack = useCallback(() => {
    (async () => {
      try {
        const zeroedStartDate = moment(state.currentSliderDate - (4 * ONEDAY)).utc().hour(0).minute(0).second(0).millisecond(0)
        const varRequest = {
          stationId: id.toString(),
          zoomLevel: 5,
          startDate: zeroedStartDate.utc().format(),
          endDate: moment(state.currentSliderDate + (3 * ONEDAY) + (30 * ONEMIN) - ONESEC).utc().format(),
        	page: 0,
					perPage: 100000,
        };
        const response: any = await getWeatherObsStations(varRequest, addAlert);
        const varRequestDaily = {
          stationIds: id.toString(),
          zoomLevel: 5,
          page:0,
					perPage: 100000,
          startDate: moment(state.currentSliderDate -(3*ONEDAY)).utc().format('YYYY-MM-DD'),
          endDate: moment(state.currentSliderDate +(3*ONEDAY)).utc().format('YYYY-MM-DD'),
        };
        const responseMinMax: any = await getWeatherMinMax(varRequestDaily, addAlert);

        if ( response.data?.length>0 && responseMinMax.data?.length>0) {
          const sortedArray = response.data.sort((a:any, b:any) => moment(a.observation_time).valueOf() - moment(b.observation_time).valueOf());
          const responseFirstObj = moment(sortedArray[0]?.observation_time).utc().set({minute:0, second:0, millisecond:0}).format();
          const responseLastObj = moment(sortedArray[sortedArray.length-1]?.observation_time).utc().set({minute:0, second:0, millisecond:0}).format();
          const fill = responseFirstObj!==responseLastObj?
            fillMissingHours(response.data,moment(state.currentSliderDate - (3 * ONEDAY)).utc().format(), moment(state.currentSliderDate + (3 * ONEDAY)).utc().format(),responseMinMax.data)
            :
            fillMissingHours(response.data,moment(state.currentSliderDate - (3 * ONEDAY)).utc().format(), responseLastObj,responseMinMax.data);
          const offset: string = stationMeta?.time_zone_offset
          const utcOffset = Number(offset);
          const currentSlideinUTC = moment.utc(state.currentSliderDate);
          const currentSlideinLST = currentSlideinUTC.utc().utcOffset(utcOffset*60);
          setCurrHr(currentSlideinLST.format('HH'));
          setCurrDate(currentSlideinLST.format('YYYY-MM-DD'));

          const convert_response = fill.map((item: any) => {
            let thisHour = item.observation_time_lst.substring(11, 13);
            let before2023 = new Date(item.observation_time_lst).getTime() < new Date("2023-01-01").getTime();
            let displayHourDateTime = item.display_hour_lst;
            let displayDate = item.display_hour_lst.substring(0, 10);
            let displayHour = item.display_hour_lst.substring(11, 13);
            let tempColors = backgroundColor(item.temperature===null? -2000: Number(item.temperature), temperatureColors);
            let rhColors = backgroundColor(item.relative_humidity===null? -2000: Number(item.relative_humidity), humidityColors);
            let precipColors = backgroundColor(item.hourly_precip===null? -2000:Number(item.hourly_precip), precip);
            let wsColors = backgroundColor(item.wind_speed===null? -2000:Number(item.wind_speed), windGustColors);
            let gsColors = backgroundColor(item.peak_gust_speed===null? -2000: Number(item.peak_gust_speed), windGustColors);
            let srColors = backgroundColor(item.sol_rad===null? -2000:Number(item.sol_rad), solarRadiationColors);
            let sfColors = SFColors(item.snow_flag===null? "null":item.snow_flag)
            let vpd_colors = backgroundColor(item.vapor_press_def===null? -2000: Number(item.vapor_press_def), vpdColors);

            return (
              {
                ...item,
                before2023: before2023,
                observation_lst_hour: thisHour,
                lst_display_hour_datetime: displayHourDateTime,
                lst_display_date: displayDate,
                lst_display_hour: displayHour,
                dateNum: Number(moment(item.observation_time_lst)),
                tempBackground: tempColors.bg,
                tempTextColor: tempColors.text,
                rhBackground: rhColors.bg,
                rhTextColor: rhColors.text,
                precipBackground: precipColors.bg,
                precipTextColor: precipColors.text,
                wsTextColor: wsColors.text,
                wsBackground: wsColors.bg,
                gsBackground: gsColors.bg,
                gsTextColor: gsColors.text,
                srBackground: srColors.bg,
                srTextColor: srColors.text,
                sfBackground: sfColors.bg,
                sfTextColor: sfColors.text,
                vpdBackground: vpd_colors.bg,
                vpdTextColor: vpd_colors.text,
              }
            )
          });
          const sorted = convert_response.sort(({ dateNum: a }, { dateNum: b }) => a - b);
          setModifiedContents(sorted)
        }
        else{
          const responseFirstObj = moment(state.currentSliderDate - (3 * ONEDAY) - (30 * ONEMIN)).utc().set({minute:0, second:0, millisecond:0}).format();
          const responseLastObj = moment(state.currentSliderDate + (3 * ONEDAY) + (30 * ONEMIN) - ONESEC).utc().set({minute:0, second:0, millisecond:0}).format();
          const fill = responseFirstObj!==responseLastObj?
            fillMissingHours(response.data,moment(state.currentSliderDate - (3 * ONEDAY)).utc().format(), moment(state.currentSliderDate + (3 * ONEDAY)).utc().format(),responseMinMax.data)
            :
            fillMissingHours(response.data,moment(state.currentSliderDate - (3 * ONEDAY)).utc().format(), responseLastObj,responseMinMax.data);
          const offset: string = stationMeta?.time_zone_offset
          const utcOffset = Number(offset);
          const currentSlideinUTC = moment.utc(state.currentSliderDate);
          const currentSlideinLST = currentSlideinUTC.utc().utcOffset(utcOffset*60);
          setCurrHr(currentSlideinLST.format('HH'));
          setCurrDate(currentSlideinLST.format('YYYY-MM-DD'));

          const convert_response = fill.map((item: any) => {
            let thisHour = item.observation_time_lst.substring(11, 13);
            let before2023 = new Date(item.observation_time_lst).getTime() < new Date("2023-01-01").getTime();
            let displayHourDateTime = item.display_hour_lst;
            let displayDate = item.display_hour_lst.substring(0, 10);
            let displayHour = item.display_hour_lst.substring(11, 13);
            let tempColors = backgroundColor(item.temperature===null? -2000: Number(item.temperature), temperatureColors);
            let rhColors = backgroundColor(item.relative_humidity===null? -2000: Number(item.relative_humidity), humidityColors);
            let precipColors = backgroundColor(item.hourly_precip===null? -2000:Number(item.hourly_precip), precip);
            let wsColors = backgroundColor(item.wind_speed===null? -2000:Number(item.wind_speed), windGustColors);
            let gsColors = backgroundColor(item.peak_gust_speed===null? -2000: Number(item.peak_gust_speed), windGustColors);
            let srColors = backgroundColor(item.sol_rad===null? -2000:Number(item.sol_rad), solarRadiationColors);
            let sfColors = SFColors(item.snow_flag===null? "null":item.snow_flag)
            let vpd_colors = backgroundColor(item.vapor_press_def===null? -2000: Number(item.vapor_press_def), vpdColors);

            return (
              {
                ...item,
                before2023: before2023,
                observation_lst_hour: thisHour,
                lst_display_hour_datetime: displayHourDateTime,
                lst_display_date: displayDate,
                lst_display_hour: displayHour,
                dateNum: Number(moment(item.observation_time_lst)),
                tempBackground: tempColors.bg,
                tempTextColor: tempColors.text,
                rhBackground: rhColors.bg,
                rhTextColor: rhColors.text,
                precipBackground: precipColors.bg,
                precipTextColor: precipColors.text,
                wsTextColor: wsColors.text,
                wsBackground: wsColors.bg,
                gsBackground: gsColors.bg,
                gsTextColor: gsColors.text,
                srBackground: srColors.bg,
                srTextColor: srColors.text,
                sfBackground: sfColors.bg,
                sfTextColor: sfColors.text,
                vpdBackground: vpd_colors.bg,
                vpdTextColor: vpd_colors.text,
              }
            )
          });
          const sorted = convert_response.sort(({ dateNum: a }, { dateNum: b }) => a - b);
          setModifiedContents(sorted)
        }

      }
      catch (error) {
        console.log('error', error)
        addAlert({
          id: '',
          message: alertMessages.retrievingStationData,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        })
      }
    })();
  }, [addAlert, state.currentSliderDate, id, fillMissingHours, stationMeta?.time_zone_offset ]);

  useEffect(() => {
    if(stationMeta?.time_zone){
      weatherCallBack();
    }
  }, [weatherCallBack, stationMeta?.time_zone]);

  useEffect(() => {
    requestAnimationFrame(scrollToCurrentHour);
  }, [modifiedContents]);

  const scrollToCurrentHour = () =>{
    const element = document.getElementById('currentTime') as HTMLElement | null;
    const container = scrollRef.current;

    if(!element || !container) return;
    const elementRight = element.offsetLeft + element.offsetWidth;
    const containerWidth = container.offsetWidth;
    const left = elementRight - containerWidth;

    container.scrollTo({
      left,
      behavior: "smooth"
    });
  };
    const goToTable = () =>{
      window.open(`${ROUTE_PATHS.table}/weather/${stationMeta.station_id}`, '_blank')
    };
  return (
    <div>
      <Grid row gap="md">
        <Grid desktop={{ col: 8 }} style={{ paddingBottom: '5px' }}>

          <Grid
            desktop={{ col: 12 }} style={{ paddingBottom: '5px' }}>
            <Button
              variant="contained"
              style={{ fontSize: '12px', width: '100%' }}
              onClick={() => scrollToCurrentHour()}
            >
              <KeyboardDoubleArrowLeftIcon /> Scroll to Current Observation <KeyboardDoubleArrowRightIcon />
            </Button>
          </Grid>
        </Grid>
        <Grid desktop={{ col: 4 }} style={{ paddingBottom: '5px' }}>
          <Button onClick={goToTable} style={{ textTransform: 'capitalize' }}>Go To Table <LaunchIcon fontSize='small' /></Button>
        </Grid>
      </Grid>
    <TableContainer ref={scrollRef} style={{ maxHeight: 350, overflowX: "auto", overflowY: 'hidden'}} className="smooth-scrollbar">
      <Table className='table-popup' style={{ tableLayout: "auto" }} >
        <TableHead style={{ height: '0px', position: 'sticky', zIndex: 100, left: 0 }}>
          <TableRow>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '31px',position: 'sticky', zIndex: 50, top:0, backgroundColor:'#A1A1A1'}}>DATE/HOUR</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '30px',position: 'sticky', width:'100px', zIndex: 50, top:'30px', backgroundColor:'#A1A1A1'}}>HR:MIN (LST)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '30px', backgroundColor:'#A1A1A1'}}>TEMP (°F)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '30px', backgroundColor:'#A1A1A1'}}>RH (%)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '30px', backgroundColor:'#A1A1A1'}}>PRECIP (IN)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '30px', backgroundColor:'#A1A1A1'}}>WS (MPH)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '30px', backgroundColor:'#A1A1A1'}}>WD (°)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '30px', backgroundColor:'#A1A1A1'}}>GS (MPH)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '30px', backgroundColor:'#A1A1A1'}}>SR (W/m2)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '33px', backgroundColor:'#A1A1A1'}}>VPD (Pa)</TableCell>
            <TableCell style={{border: '1px solid #bbbbbb', fontWeight: 700, fontSize:'12px', height: '33px', backgroundColor:'#A1A1A1'}}>SF</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow style={{width: '70px'}}/>
          {/*IMPORTANT: these table rows are actually columns- updated in Stations.scss*/}
          {modifiedContents?.length > 0 &&
            modifiedContents.map((item: any, y) => (

              <TableRow style={{ width: '100px', }}
                key ={`${item.lst_display_date}-${y}`}
                id={item.lst_display_date === (currDate) && item.lst_display_hour.toString() === (currHr) ? 'currentTime' : ''}
                className={item.lst_display_date === (currDate) && item.lst_display_hour.toString() === (currHr) ? (item.observation_type === 'F' ? 'selected-popup-dashed' : 'selected-popup') : ''}
              >
                <TableCell style={{ fontSize: '12px', height: '30px',position: 'sticky', zIndex: 50, top:0, backgroundColor: 'white'}}
                  className={item.observation_type === 'F' ? 'forecast' : 'obs'}
                  key={`${item.lst_display_hour_datetime}-${y}`}>{formatDate(item.lst_display_hour_datetime)}</TableCell>

                {item.before2023 ?
                  (
                    <TableCell
                      className={item.observation_type === 'F' ? 'forecast' : 'obs'}
                      key={`${item.observation_lst_hour}-before2023`} style={{
                        height: '30px'
                      }}>
                      {item.observation_lst_hour.toString().length === 2 ? item.observation_lst_hour : "0" + item.observation_lst_hour+ ` ${stationMeta?.time_zone}`}
                      :{formatMinute(item.observation_time_lst) + ` ${stationMeta?.time_zone}`}                    
                    </TableCell>
                  )
                  :
                  (
                    <TableCell
                      style ={{height: '30px' , position: 'sticky', zIndex: 50, top:'30px', backgroundColor: 'white',}}
                      className={item.observation_type === 'F' ? 'forecast' : 'obs'}
                      key={`${item.observation_lst_hour}-${y}`} 
                    >
                      {item.observation_lst_hour.toString().length === 2 ? item.observation_lst_hour : "0" + item.observation_lst_hour+ ` ${stationMeta?.time_zone}`}
                      :{formatMinute(item.observation_time_lst) + ` ${stationMeta?.time_zone}`}
                    </TableCell>
                  )
                }
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`temphr-${item.temperature}--${item.tempBackground}-${y}`} style={item.tempBackground ? { height: '30px', backgroundColor: item.tempBackground, color: item.tempTextColor } : { height: '30px' }}>{item.temperature === null ? "●●●" : item.temperature} </TableCell>
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`rhhr-${item.relative_humidity}-${item.rhBackground}--${y}`} style={item.rhBackground ? { height: '30px', backgroundColor: item.rhBackground, color: item.rhTextColor } : { height: '30px' }}>{item.relative_humidity === null ? "●●●" : item.relative_humidity} </TableCell>
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`precipHr-${item.hourly_precip}-${item.precipBackground}-${y}-`} style={item.precipBackground ? { height: '30px', backgroundColor: item.precipBackground, color: item.precipTextColor } : { height: '30px' }}>{item.hourly_precip === null ? "●●●" : item.hourly_precip} </TableCell>
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`wshr-${item.wind_speed}-${item.wsBackground}-${y}`} style={item.wsBackground ? { height: '30px', backgroundColor: item.wsBackground, color: item.wsTextColor } : { height: '30px' }}>{item.wind_speed === null ? "●●●" : item.wind_speed} </TableCell>
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`wdhr-${item.wind_direction}---${item.station_id}-${y}`} style={{ height: '30px' }}>{item.wind_direction === null ? "●●●" : <img src={arrow} style={{ width: '15px', transform: 'rotate(' + windDirectionChange(item.wind_direction - 90) + 'deg)' }} alt={`${item.wind_direction} at ${item.observation_lst_hour}`} />} </TableCell>
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`gusthr${item.peak_gust_speed}-${item.gsBackground}---${y}`} style={item.gsBackground ? { height: '30px', backgroundColor: item.gsBackground, color: item.gsTextColor } : { height: '30px' }}>{item.peak_gust_speed === null ? "●●●" : item.peak_gust_speed}</TableCell>
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`srHr-${item.sol_rad}-${item.srBackground}-${y}---`} style={item.srBackground ? { height: '30px', backgroundColor: item.srBackground, color: item.srTextColor } : { height: '30px' }}>{item.sol_rad === null ? "●●●" : item.sol_rad}</TableCell>
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`vpdHr-${item.vapor_press_def}-${item.vpdBackground}-${y}---`} style={item.vpdBackground ? { height: '30px', backgroundColor: item.vpdBackground, color: item.vpdTextColor } : { height: '30px' }}>{item.vapor_press_def === null ? "●●●" : item.vapor_press_def}</TableCell>
                <TableCell className={item.observation_type === 'F' ? 'forecast' : 'obs'} key={`sfHr-${item.snow_flag}--${item.sfBackground}--${y}`} style={item.sfBackground ? { height: '30px', backgroundColor: item.sfBackground, color: item.sfTextColor } : { height: '30px' }}>{item.snow_flag === null ? "●●●" : item.snow_flag}</TableCell>

              </TableRow>
            ))
          }
          </TableBody>

      </Table>
    </TableContainer>
    </div>
  )
}